import { ReactElement, useMemo } from "react";
import { Menu, MenuItem, Divider, Tooltip } from "@mui/material";
import { TransactionWithDate } from "../../../utils/transactionProcessing";

interface TransactionMenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  selectedTransaction: TransactionWithDate | null;
  canViewTransactionHistory: () => boolean;
  canDeleteTransactions: () => boolean;
  canEditTransactions: () => boolean;
  isProtectedAdjustment: (transaction: TransactionWithDate | null) => boolean;
  usesArchivedAccount: (transaction: TransactionWithDate) => boolean;
  getArchivedAccountNames: (transaction: TransactionWithDate) => string[];
  onEdit: () => void;
  onDuplicate: () => void;
  onCopyUrl: () => void;
  onHistory: () => Promise<void>;
  onDelete: () => void;
}

function TransactionMenu({
  anchorEl,
  open,
  onClose,
  selectedTransaction,
  canViewTransactionHistory,
  canDeleteTransactions,
  canEditTransactions,
  isProtectedAdjustment,
  usesArchivedAccount,
  getArchivedAccountNames,
  onEdit,
  onDuplicate,
  onCopyUrl,
  onHistory,
  onDelete,
}: TransactionMenuProps): ReactElement {
  // Calculate permission and disabled state once per render to avoid repeated calls
  const canDelete = useMemo(() => canDeleteTransactions(), [canDeleteTransactions]);
  const canEdit = useMemo(() => canEditTransactions(), [canEditTransactions]);
  const isProtected = useMemo(() => isProtectedAdjustment(selectedTransaction), [isProtectedAdjustment, selectedTransaction]);
  const usesArchived = useMemo(() => selectedTransaction ? usesArchivedAccount(selectedTransaction) : false, [usesArchivedAccount, selectedTransaction]);
  const noPermission = !canDelete;
  const isDeleteDisabled = isProtected || usesArchived || noPermission;
  
  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "right",
      }}
      transformOrigin={{
        vertical: "top",
        horizontal: "right",
      }}
      MenuListProps={{
        "aria-labelledby": "transaction-menu-button",
      }}
      PaperProps={{
        sx: {
          maxHeight: "400px",
          overflow: "auto",
          zIndex: 1300, // Ensure menu is above other elements
        },
      }}
      // Ensure menu positions correctly relative to anchor
      disablePortal={false}
      // Add positioning modifiers to handle edge cases
      slotProps={{
        paper: {
          elevation: 8,
        },
      }}
    >
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
          onClose();
        }}
        disabled={!canEdit || isProtectedAdjustment(selectedTransaction) || (selectedTransaction ? usesArchivedAccount(selectedTransaction) : false)}
        title={
          !canEdit
            ? "You don't have permission to edit transactions."
            : selectedTransaction && usesArchivedAccount(selectedTransaction)
            ? `The account "${getArchivedAccountNames(selectedTransaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be edited.`
            : undefined
        }
      >
        Edit
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          onDuplicate();
          onClose();
        }}
        disabled={!canEdit || isProtectedAdjustment(selectedTransaction) || (selectedTransaction ? usesArchivedAccount(selectedTransaction) : false)}
        title={
          !canEdit
            ? "You don't have permission to edit transactions."
            : selectedTransaction && usesArchivedAccount(selectedTransaction)
            ? `The account "${getArchivedAccountNames(selectedTransaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be duplicated.`
            : undefined
        }
      >
        Duplicate
      </MenuItem>
      <MenuItem
        onClick={(e) => {
          e.stopPropagation();
          onCopyUrl();
          onClose();
        }}
        disabled={!selectedTransaction}
      >
        Copy URL
      </MenuItem>
      {canViewTransactionHistory() && (
        <MenuItem
          onClick={async (e) => {
            e.stopPropagation();
            await onHistory();
            onClose();
          }}
          disabled={!selectedTransaction}
        >
          History
        </MenuItem>
      )}
      <Divider />
      <Tooltip
        title={
          usesArchived && selectedTransaction
            ? `The account "${getArchivedAccountNames(selectedTransaction).join('", "')}" used for this transaction is currently archived, so this transaction cannot be deleted.`
            : noPermission
            ? "You do not have permission to delete transactions. This permission can be configured in Settings > Permissions."
            : ""
        }
        disableHoverListener={!isProtected && !usesArchived && canDelete}
      >
        <span>
          <MenuItem
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
              // Don't call onClose here - onDelete already closes the menu
              // and we need to keep selectedTransaction for the delete dialog
            }}
            sx={{ color: "error.main" }}
            disabled={isDeleteDisabled}
          >
            Delete
          </MenuItem>
        </span>
      </Tooltip>
    </Menu>
  );
}

export default TransactionMenu;
